import React from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, InputAdornment, List, ListItemButton, ListItemText,
  Stack, Switch, TextField, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { stargraveAlienArtefacts } from '../../data/stargrave/alienArtefacts';

interface StargraveAlienArtefactModalProps {
  open: boolean;
  availableCredits: number;
  onClose: () => void;
  onAdd: (artefactId: string, addForFree: boolean) => void;
}

export const StargraveAlienArtefactModal: React.FC<StargraveAlienArtefactModalProps> = ({
  open, availableCredits, onClose, onAdd,
}) => {
  const [selectedId, setSelectedId] = React.useState('');
  const [filter, setFilter] = React.useState('');
  const [addForFree, setAddForFree] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const selected = stargraveAlienArtefacts.find((artefact) => artefact.id === selectedId);
  const displayed = stargraveAlienArtefacts
    .filter((artefact) => !filter.trim() || `${artefact.name} ${artefact.summary}`.toLowerCase().includes(filter.trim().toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));
  const close = () => { setSelectedId(''); setFilter(''); setAddForFree(false); setError(null); onClose(); };
  const add = () => {
    if (!selected) return setError('Select an Alien Artefact.');
    if (!addForFree && selected.cost > availableCredits) return setError('Not enough credits.');
    onAdd(selected.id, addForFree);
    close();
  };

  return <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
    <DialogTitle>Add Alien Artefact</DialogTitle>
    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
      {error && <Alert severity="error">{error}</Alert>}
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={1}>
        <Typography color="text.secondary">Available: {availableCredits}cr</Typography>
        <FormControlLabel control={<Switch checked={addForFree} onChange={(event) => setAddForFree(event.target.checked)} />} label="Add found artefact" />
      </Stack>
      <TextField size="small" value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="Search Alien Artefacts"
        slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }} />
      <List dense sx={{ maxHeight: 340, overflowY: 'auto', border: 1, borderColor: 'divider' }}>
        {displayed.map((artefact) => <ListItemButton key={artefact.id} selected={selectedId === artefact.id}
          disabled={!addForFree && artefact.cost > availableCredits} onClick={() => { setSelectedId(artefact.id); setError(null); }}>
          <ListItemText primary={artefact.name} secondary={`Buy ${artefact.cost}cr | Sell ${artefact.salePrice}cr`} />
        </ListItemButton>)}
      </List>
      {selected && <Box>
        <Typography variant="h6">{selected.name}</Typography>
        <Typography variant="body2" sx={{ my: 0.5 }}>{selected.summary}</Typography>
        <Typography variant="caption" color="text.secondary">Captain or First Mate only | 1 gear slot | Core book p. {selected.sourcePage}</Typography>
      </Box>}
    </DialogContent>
    <DialogActions>
      <Button onClick={close}>Cancel</Button>
      <Button variant="contained" onClick={add} disabled={!selected}>Add Artefact</Button>
    </DialogActions>
  </Dialog>;
};
